import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Cloud, CheckCircle, XCircle, LogOut } from 'lucide-react'
import { api } from '@/lib/api'
import { startOneDriveAuth, handleOneDriveCallback } from '@/lib/onedrive'

export default function Settings() {
  const [params, setParams] = useSearchParams()
  const qc = useQueryClient()
  const code = params.get('code')

  const { data: odStatus, isLoading } = useQuery({
    queryKey: ['onedrive-status'],
    queryFn: api.onedrive.status,
  })

  const disconnectMutation = useMutation({
    mutationFn: api.onedrive.disconnect,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onedrive-status'] }),
  })

  // Handle OAuth callback
  useEffect(() => {
    if (code) {
      handleOneDriveCallback(code)
        .then(() => {
          qc.invalidateQueries({ queryKey: ['onedrive-status'] })
          setParams({}, { replace: true })
        })
        .catch(console.error)
    }
  }, [code, qc, setParams])

  return (
    <div className="space-y-4">
      <section className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        <div className="p-4">
          <h3 className="text-sm font-semibold text-slate-800">OneDrive Integration</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Attach files directly from your personal OneDrive to any record.
          </p>
        </div>

        <div className="p-4 flex items-center gap-3">
          <Cloud size={20} className="text-brand-500 flex-shrink-0" />
          <div className="flex-1">
            {isLoading ? (
              <p className="text-sm text-slate-400">Checking connection…</p>
            ) : odStatus?.connected ? (
              <>
                <div className="flex items-center gap-1.5">
                  <CheckCircle size={14} className="text-green-500" />
                  <p className="text-sm font-medium text-slate-800">Connected</p>
                </div>
                {odStatus.email && <p className="text-xs text-slate-400 mt-0.5">{odStatus.email}</p>}
              </>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <XCircle size={14} className="text-slate-300" />
                  <p className="text-sm font-medium text-slate-500">Not connected</p>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">Connect to browse and attach OneDrive files</p>
              </>
            )}
          </div>

          {odStatus?.connected ? (
            <button
              onClick={() => { if (confirm('Disconnect OneDrive?')) disconnectMutation.mutate() }}
              className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-600 px-3 py-2 rounded-lg hover:bg-red-50"
            >
              <LogOut size={14} /> Disconnect
            </button>
          ) : (
            <button
              onClick={() => startOneDriveAuth()}
              className="btn-primary px-4 py-2 text-sm"
            >
              Connect
            </button>
          )}
        </div>

        {!odStatus?.connected && (
          <div className="px-4 py-3 bg-slate-50 rounded-b-xl">
            <p className="text-xs text-slate-500">
              <strong>Setup required:</strong> Register an app at{' '}
              <a href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noreferrer" className="text-brand-600 underline">
                portal.azure.com
              </a>
              , add <code className="bg-white px-1 rounded text-xs">{window.location.origin}/settings?tab=onedrive</code> as a redirect URI, then add your Client ID to <code className="bg-white px-1 rounded text-xs">wrangler.toml</code>.
            </p>
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        <div className="p-4">
          <h3 className="text-sm font-semibold text-slate-800">Authentication</h3>
          <p className="text-xs text-slate-400 mt-0.5">App access is controlled by Cloudflare Access.</p>
        </div>
        <div className="p-4 text-xs text-slate-500 space-y-1">
          <p>Your Microsoft personal account is used for app login via Cloudflare Zero Trust.</p>
          <p>To manage access policies, visit the <a href="https://one.dash.cloudflare.com/" target="_blank" rel="noreferrer" className="text-brand-600 underline">Cloudflare Zero Trust dashboard</a>.</p>
        </div>
      </section>
    </div>
  )
}
